import { AxiosResponse } from "axios";
import { API } from "../index";
import { Page } from "../type";
import { buildUserOrdersByDatePath } from "./packageOrderQuery.mjs";

// Package related types
export interface PackageItem {
  id: number;
  name: string;
  tagline?: string | null;
  description: string;
  price: number;
  image?: string;
  type?: string;
  tax_free?: boolean;
}

export interface LodgePackage {
  id: number;
  name: string;
  tagline?: string | null;
  descript: string;
  price: number;
  count: number;
  has_spots: boolean;
  images: string[] | Array<{
    altText: string;
    caption: string;
    key: number;
    src: string;
  }>;
  seat_img?: string;
  thumbnail?: string;
  detail_img?: string;
  state: string;
  min_date?: string;
  max_date?: string;
  std_personnel?: number;
  max_personnel?: number;
  using_info?: string;
  instruction?: string;
  allow_free_booking?: boolean;
  max_personnel_day?: number;
  priority?: number;
  option_required_type?: string;
  order_available_start_at?: string;
  option_required?: boolean;
  option_available_only_when_reservation?: boolean;
  spots?: Array<{
    id: number;
    name: string;
    is_free: boolean;
    is_able: boolean;
    reason?: string;
  }>;
}

export interface PackageReservation {
  id: number;
  package_id: string;
  예약신청일: string;
  예약상품: string;
  자리: string;
  예약자: string;
  번호: string;
  예약시작일: string;
  예약종료일: string;
  이용기간: number;
  예약상태: string;
  결제상태: string;
  승인번호: string;
  결제금액: number;
  gifted: string;
  assigned_seat?: {
    id: number;
    name: string;
  } | null;
  options: Array<{
    id: number;
    name: string;
    price: number;
    state: string;
    type: string;
    image: string;
    description: string;
  }>;
  method?: string;
}

export interface PackageOption {
  id: number;
  name: string;
  price: number;
  description: string;
  type: string;
  image: string;
  tax_free: boolean;
}

export interface ReservationGift {
  id: number;
  name: string;
  spot: string;
  sender: string;
  phone_num: string;
  recipient: string;
  recipient_phone_num: string;
  reserv_id: string;
  reservationDate: string;
  reservationEndDate: string;
  reserv_state: string;
  gift_state: string;
  send_date: string;
  subdomain: string;
  cancelAmount: number;
  effectiveCancelAmount?: number;
  amount: number;
  package_id: string;
  descript: string;
  image: string;
  options: Array<{
    id: number;
    name: string;
    price: number;
    state: string;
    type: string;
    image: string;
    description: string;
  }>;
  depositRequired: {
    id: number;
    name: string;
    price: number;
    type: string;
    image: string;
    description: string;
  } | null;
  precoupon_used: boolean;
}

export interface UserOrder {
  id: number;
  order_id: string;
  user?: {
    id: number;
    username: string;
    phone_num: string;
  };
  name?: string;
  package_id?: number;
  spot?: string;
  spot_id?: number;
  state: string;
  amount: number;
  order_date?: string;
  orderDate?: string;
  reservationDate?: string;
  reservationEndDate?: string;
  using_info?: string;
  instruction?: string;
  approve_num?: string;
  receipt_url?: string;
  pg_provider?: string;
  reservations?: Array<{
    id: number;
    state: string;
    start_date: string;
    end_date: string;
    assigned_seat?: {
      id: number;
      name: string;
    } | null;
    spot: {
      id: number;
      name: string;
      package: {
        id: number;
        name: string;
      };
    };
  }>;
}

export interface UserOrderAppListResponse {
  results: Array<UserOrder>;
  total: number;
  page: number;
  page_size: number;
  total_page: number;
  hasMore: boolean;
}

export const packageService = {
  getPackageItemById: async (id: string): Promise<AxiosResponse<PackageItem>> => {
    return await API.get(`/package/items/${id}/`);
  },

  getPackageItemsByDate: async (startDate: string, id: string): Promise<AxiosResponse<Array<PackageItem>>> => {
    return await API.get(`/package/items/?date=${startDate}&id=${id}`);
  },

  getLodgePackageDetail: async (id: string, startDate: string, endDate: string): Promise<AxiosResponse<LodgePackage>> => {
    return await API.get(`/package/lodging/${id}/?start_date=${startDate}&end_date=${endDate}`);
  },

  getPackagesByDateRange: async (startDate: string, endDate: string, subdomain: string, membership_product_id?: string): Promise<AxiosResponse<Array<LodgePackage>>> => {
    return await API.get(`/package/?start_date=${startDate}&end_date=${endDate}&subdomain=${subdomain}${membership_product_id ? `&membership_product_id=${membership_product_id}` : ""}`);
  },

  getLodgePackagePrice: async (id: string, startDate: string, endDate: string, spot_str: string): Promise<AxiosResponse<{ 
    price: number; 
    discount?: number;
    total_price: number;
    options_required: boolean;
    option_required_type?: string;
  }>> => {
    return await API.get(`/package/get-lodge-package-price/?package_id=${id}&start_date=${startDate}&end_date=${endDate}&spots=${spot_str}`);
  },

  getReservationById: async (id: string): Promise<AxiosResponse<{
    reservation: PackageReservation;
    is_gift: boolean;
    gift_status?: string;
  }>> => {
    return await API.get(`/package/get-reservation/${id}/`);
  },

  getOptionListByReservId: async (reservId: string): Promise<AxiosResponse<Array<PackageOption>>> => {
    return await API.get(`/package/option-list/?reserv_id=${reservId}`);
  },

  getOptionListByOrderId: async (orderId: string): Promise<AxiosResponse<Array<PackageOption>>> => {
    return await API.get(`/package/option-list/?order_id=${orderId}`);
  },

  cancelReservationOptionsByIds: async (reservationOptionIds: Array<number | string>): Promise<AxiosResponse<{ result?: string; error?: string }>> => {
    return await API.post("/package/cancel-reservation-options/", {
      reserv_options: reservationOptionIds.map((id) => ({ id })),
    });
  },

  inviteUserForReservation: async (reservId: number, phoneNum: string): Promise<{ success: boolean; message?: string }> => {
    const phone_num = phoneNum.replace(/-/g, "");
    const result = await API.post(
      `/package/invite-user-for-gift/${reservId}/`,
      {
        reservId: reservId,
        phoneNum: phone_num,
      }
    );
    return result.data;
  },

  giftReservationToKakaoFriend: async (reservId: number, kakaoId: string): Promise<{ success: boolean; message?: string }> => {
    const result = await API.post(
      `/package/send-reservation-gift-kakao/${reservId}/`,
      {
        reservId: reservId,
        kakaoId: kakaoId,
      }
    );
    return result.data;
  },

  giftReservationByUrl: async (reservId: number): Promise<{ result?: string; gift_id: string | number }> => {
    const result = await API.post(
      `/package/send-reservation-gift-url/`,
      {
        reservationId: reservId,
      }
    );
    return result.data;
  },

  useReservationGift: async (giftId: number): Promise<{ result?: string; success?: boolean; message?: string }> => {
    const result = await API.put(
      `/package/use-reservation-gift/${giftId}/`
    );
    return result.data;
  },

  getReservationQRCode: async (reservId: number): Promise<{ 
    qr_code: string;
    reservation_id: number;
    checkin_date?: string;
    is_valid: boolean;
  }> => {
    const result = await API.get(
      `/package/reservation-qr/${reservId}/`
    );
    return result.data;
  },

  getReservationGifts: async (phone: string, date?: string): Promise<AxiosResponse<Array<ReservationGift>>> => {
    return await API.get(`/package/reservation-gift/`, {
      params: {
        recipient: phone,
        date: date
      }
    });
  },

  putReceiveReservationById: async (giftId: number): Promise<{ success: boolean; message?: string }> => {
    const result = await API.put(
      `/package/receive-reservation-gift/${giftId}/`,
    );
    return result.data;
  },

  deleteReservationGiftById: async (reservId: number): Promise<AxiosResponse<{ success: boolean; message?: string }>> => {
    const result = await API.delete(
      `/package/cancel-reservation-gift/${reservId}/`
    );
    return result;
  },
};

export const getUserOrdersByDate = async (date: string, filterSite?: string): Promise<AxiosResponse<UserOrderAppListResponse>> => {
  return API.get(buildUserOrdersByDatePath(date, filterSite));
};

export const getUserOrdersByOrderId = async (orderId: string): Promise<AxiosResponse<UserOrderAppListResponse>> => {
  return API.get(`/package/user-order-app/?order_id=${encodeURIComponent(orderId)}`);
}

export const getOrderListByPage = async (pageParam: Page): Promise<AxiosResponse<{
  count: number;
  next: string | null;
  previous: string | null;
  results: Array<UserOrder>;
  active_state_count?: number;
  completed_state_count?: number;
  canceled_state_count?: number;
}>> => {
  const response = await API.get(
    `/package/user-order-app/?page=${pageParam.page}${pageParam.queryString}`,
    {}
  );
  return response;
}

export const processOrderFreeBooking = async (orderId: string, amount: string): Promise<AxiosResponse<{
  success: boolean;
  order_id?: string;
  message?: string;
}>> => {
  return API.post(`/package/order-free-booking/`, {
    orderId: orderId,
    amount: amount
  });
}
